import * as React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

type ToggleButtonValue = string | number;
type ToggleButtonSize = 'small' | 'medium' | 'large';
type ToggleButtonItemSize = ToggleButtonSize | 'icon';
type ToggleButtonGroupContext = 'default' | 'modal';

interface ToggleButtonGroupBaseProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onChange'
> {
  disabled?: boolean;
  size?: ToggleButtonSize;
  context?: ToggleButtonGroupContext;
  sx?: React.CSSProperties;
}

type ToggleButtonGroupExclusiveProps<T extends ToggleButtonValue> = {
  exclusive: true;
  value?: T | null;
  onChange?(event: React.MouseEvent<HTMLElement>, value: T | null): void;
};

type ToggleButtonGroupMultipleProps<T extends ToggleButtonValue> = {
  exclusive?: false;
  value?: T[];
  onChange?(event: React.MouseEvent<HTMLElement>, value: T[]): void;
};

type ToggleButtonGroupProps<T extends ToggleButtonValue = ToggleButtonValue> =
  ToggleButtonGroupBaseProps &
    (ToggleButtonGroupExclusiveProps<T> | ToggleButtonGroupMultipleProps<T>);

interface ToggleButtonGroupContextValue {
  value: unknown;
  exclusive: boolean;
  disabled: boolean;
  size: ToggleButtonSize;
  onItemToggle: (
    event: React.MouseEvent<HTMLElement>,
    itemValue: ToggleButtonValue
  ) => void;
}

const ToggleButtonGroupContext =
  React.createContext<ToggleButtonGroupContextValue | null>(null);

const groupSizeClasses: Record<ToggleButtonSize, string> = {
  small: 'h-10 rounded-md p-1',
  medium: 'h-12 rounded-md p-1',
  large: 'h-14 rounded-md p-1',
};

const groupContextClasses: Record<ToggleButtonGroupContext, string> = {
  default: '',
  modal:
    "!bg-[var(--color-modal-field)] [&_[data-slot='toggle-button']]:!bg-transparent [&_[data-slot='toggle-button'][data-toggle-state=off]:hover]:!bg-[var(--color-neutral-0-10)] [&_[data-slot='toggle-button'][data-toggle-state=off]:focus-visible]:!bg-[var(--color-neutral-0-10)] [&_[data-slot='toggle-button'][data-toggle-state=on]]:!bg-[var(--color-modal-inverse)] [&_[data-slot='toggle-button'][data-toggle-state=on]]:!text-[var(--color-modal-heading)] [&_[data-slot='toggle-button']]:!text-neutral-0 border border-solid border-[var(--color-neutral-0-30)]",
};

const buttonSizeClasses: Record<ToggleButtonSize, string> = {
  small: 'h-8 rounded-sm px-3 text-xs',
  medium: 'h-10 rounded-sm px-4 text-sm',
  large: 'h-12 rounded-sm px-5 text-base',
};

const buttonIconSizeClasses: Record<ToggleButtonSize, string> = {
  small: 'h-8 w-8 rounded-sm p-0',
  medium: 'h-10 w-10 rounded-sm p-0',
  large: 'h-12 w-12 rounded-sm p-0',
};

const selectedButtonRadiusClasses: Record<ToggleButtonSize, string> = {
  small: '!rounded-sm',
  medium: '!rounded-sm',
  large: '!rounded-sm',
};

const ToggleButtonGroupInner = <
  T extends ToggleButtonValue = ToggleButtonValue,
>(
  {
    className,
    value = null,
    exclusive = false,
    onChange,
    disabled = false,
    size = 'medium',
    context = 'default',
    style,
    sx,
    children,
    ...props
  }: ToggleButtonGroupProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>
) => {
  const onItemToggle = React.useCallback(
    (event: React.MouseEvent<HTMLElement>, itemValue: ToggleButtonValue) => {
      if (!onChange || disabled) {
        return;
      }

      if (exclusive) {
        const currentValue = Array.isArray(value) ? value[0] : value;
        const nextValue = currentValue === itemValue ? null : (itemValue as T);

        (onChange as ToggleButtonGroupExclusiveProps<T>['onChange'])?.(
          event,
          nextValue
        );

        return;
      }

      const currentValues = Array.isArray(value)
        ? value
        : value == null
          ? []
          : [value];

      const typedItemValue = itemValue as T;
      const hasValue = currentValues.includes(typedItemValue);

      const nextValues = hasValue
        ? currentValues.filter((v) => v !== typedItemValue)
        : [...currentValues, typedItemValue];

      (onChange as ToggleButtonGroupMultipleProps<T>['onChange'])?.(
        event,
        nextValues
      );
    },
    [disabled, exclusive, onChange, value]
  );

  const contextValue = React.useMemo(
    () => ({
      value,
      exclusive,
      disabled,
      size,
      onItemToggle,
    }),
    [value, exclusive, disabled, size, onItemToggle]
  );

  return (
    <ToggleButtonGroupContext.Provider value={contextValue}>
      <div
        ref={ref}
        className={cn(
          'ui-toggle-group inline-flex w-fit items-center gap-1 border border-border-subtle bg-surface-input',
          groupSizeClasses[size],
          groupContextClasses[context],
          disabled && 'pointer-events-none opacity-50',
          className
        )}
        data-slot="toggle-group"
        data-disabled={disabled ? '' : undefined}
        data-context={context}
        style={{ ...(sx ?? {}), ...(style ?? {}) }}
        {...props}
      >
        {children}
      </div>
    </ToggleButtonGroupContext.Provider>
  );
};

const ToggleButtonGroup = React.forwardRef(ToggleButtonGroupInner) as <
  T extends ToggleButtonValue = ToggleButtonValue,
>(
  props: ToggleButtonGroupProps<T> & React.RefAttributes<HTMLDivElement>
) => React.ReactElement | null;

type ToggleButtonProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'value'
> & {
  value: ToggleButtonValue;
  selected?: boolean;
  size?: ToggleButtonItemSize;
  sx?: React.CSSProperties;
};

const ToggleButton = React.forwardRef<HTMLButtonElement, ToggleButtonProps>(
  (
    {
      className,
      value,
      selected,
      size,
      disabled = false,
      onClick,
      type = 'button',
      // Written explicitly so Safari's default keyboard mode (which skips
      // native buttons without a tabindex attribute) includes the toggle.
      tabIndex = 0,
      style,
      sx,
      children,
      ...props
    },
    ref
  ) => {
    const group = React.useContext(ToggleButtonGroupContext);
    const isDisabled = Boolean(group?.disabled || disabled);
    const groupSize = group?.size ?? 'medium';
    const resolvedSize = size ?? groupSize;

    const selectedRadiusClass =
      resolvedSize === 'icon'
        ? selectedButtonRadiusClasses[groupSize]
        : selectedButtonRadiusClasses[resolvedSize];

    const isSelected = (() => {
      if (typeof selected === 'boolean') return selected;
      if (!group) return false;
      if (group.exclusive) {
        const currentValue = Array.isArray(group.value)
          ? group.value[0]
          : group.value;

        return currentValue === value;
      }

      const currentValues = Array.isArray(group.value)
        ? group.value
        : group.value == null
          ? []
          : [group.value];

      return currentValues.includes(value);
    })();

    return (
      <Button
        ref={ref}
        type={type}
        variant="ghost"
        className={cn(
          "ui-toggle-button inline-flex items-center justify-center gap-2 whitespace-nowrap border border-transparent bg-transparent font-heading font-medium leading-none text-fg-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring-default)] disabled:pointer-events-none disabled:opacity-50 [&_[data-slot='field']]:h-full [&_[data-slot='field']]:justify-center [&_[data-slot='field']]:py-0.5 [&_[data-slot='field']>div]:h-full [&_[data-slot='field']>div]:min-h-0 [&_[data-slot='input']]:my-0 [&_[data-slot='input']]:h-full [&_[data-slot='input']]:max-h-full",
          resolvedSize === 'icon'
            ? buttonIconSizeClasses[groupSize]
            : buttonSizeClasses[resolvedSize],
          isSelected &&
            cn(
              selectedRadiusClass,
              'border-transparent bg-surface-toggle-thumb text-fg-primary hover:bg-surface-toggle-thumb hover:text-fg-primary dark:hover:bg-surface-toggle-thumb dark:hover:text-fg-primary shadow-[var(--shadow-light-only)] dark:shadow-none'
            ),
          className
        )}
        data-slot="toggle-button"
        data-state={isSelected ? 'on' : 'off'}
        data-toggle-state={isSelected ? 'on' : 'off'}
        aria-pressed={isSelected}
        tabIndex={isDisabled ? undefined : tabIndex}
        disabled={isDisabled}
        onClick={(event) => {
          if (group && !isDisabled) {
            group.onItemToggle(event, value);
          }
          onClick?.(event);
        }}
        style={{ ...(sx ?? {}), ...(style ?? {}) }}
        {...props}
      >
        {children}
      </Button>
    );
  }
);

ToggleButton.displayName = 'ToggleButton';
export { ToggleButtonGroup, ToggleButton };
